import {
  Box,
  Card,
  CardHeader,
  CardTable,
  CircularProgress,
  ColumnDefinition,
  Theme,
  makeStyles,
} from '@forgeglobal/design-framework'
import { LeadFilters, LeadsDashboardFilter } from 'components/leads/LeadsDashboardFilter'
import { LeadsTableRow } from 'components/leads/LeadsTableRow'
import { netWorth, targetCheckSize } from 'util/Enums'
import { useAppDataQuery } from 'graphql/bindings/AdminBindings'
import { useLeadActions } from 'components/leads/useLeadActions'
import { useLeadsDataSource } from './useLeadsDataSource'
import AssignModal, { useAssignModal } from 'components/leads/AssignModal'
import ProfileModal, { useProfileModal } from 'components/leads/ProfileModal'
import React, { Fragment, useEffect, useState } from 'react'
import localForage from 'localforage'

const store = localForage.createInstance({
  name: 'persist-table-state',
})

const columnDefs: ColumnDefinition[] = [
  {
    name: 'name',
    label: 'Name',
    minWidth: 240,
    priority: 1,
  },
  {
    name: 'net_worth',
    label: 'Net Worth',
    minWidth: 100,
    priority: 3,
  },
  {
    name: 'target_check_size',
    label: 'Check Size',
    minWidth: 100,
    priority: 3,
  },
  {
    name: 'email_verified',
    label: 'Email Verified',
    minWidth: 100,
    priority: 4,
  },
  {
    name: 'accredited',
    label: 'Accredited',
    minWidth: 100,
    priority: 4,
  },
  {
    name: 'bids',
    label: 'Bids',
    minWidth: 100,
    priority: 5,
  },
  {
    name: 'last_contacted_by',
    label: 'Last Contact By',
    minWidth: 200,
    priority: 6,
  },
  {
    name: 'country',
    label: 'Country of Domicile',
    minWidth: 100,
    priority: 7,
  },
  {
    name: 'date_created',
    label: 'Date Signed Up',
    minWidth: 100,
    priority: 2,
  },
]

const PAGE_SIZE = 10

const useStyles = makeStyles((theme: Theme) => ({
  backgroundShaded: {
    backgroundColor: theme.palette.background.default,
    height: '100%',
  },
  headingContainer: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  buttonAdjust: {
    marginLeft: theme.spacing(-1),
    marginTop: theme.spacing(-0.5),
  },
  nudgeUp: {
    marginTop: theme.spacing(-0.25),
  },
  iconDivider: {
    backgroundColor: '#e0e0e0',
    width: '1px',
    height: theme.spacing(4),
    display: 'inline-block',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(2),
  },
  contentArea: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  tableRow: {
    paddingTop: 10,
    paddingBottom: 10,
  },
  loading: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.2)',
    zIndex: 10,
    pointerEvents: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))

interface LeadsDashboardTableProps {
  title: string
  defaultFilters: LeadFilters
}

const calculateActiveFilterCount = (filters: LeadFilters) => {
  return Object.entries(filters).reduce<number>((acc, [key, value]) => {
    if (key === 'netWorth' && value.length !== netWorth.length) {
      return acc + 1
    }
    if (key === 'targetCheckSize' && value.length !== targetCheckSize.length) {
      return acc + 1
    }
    if (['emailVerified', 'isAccredited'].includes(key) && value) {
      return acc + 1
    }
    return acc
  }, 0)
}

const validateFilters = (filters: LeadFilters): Boolean => {
  const tcsValues = targetCheckSize.map(x => x.value)
  const tcsValid =
    Array.isArray(filters.targetCheckSize) && filters.targetCheckSize.filter(x => !tcsValues.includes(x)).length === 0

  const netWorthValues = netWorth.map(x => x.value)
  const nwValid =
    Array.isArray(filters.netWorth) && filters.netWorth.filter(x => !netWorthValues.includes(x)).length === 0

  return tcsValid && nwValid
}

export const LeadsDashboardTable: React.FC<LeadsDashboardTableProps> = props => {
  const classes = useStyles()
  const cacheKey = `lead-dashboard-filters-${props.title}`

  const { data: appDataQuery } = useAppDataQuery()
  const { currentAdmin } = appDataQuery || {}

  const [offset, setOffset] = useState(0)
  const [activeFilters, setActiveFilters] = useState<LeadFilters>(props.defaultFilters)
  const [numActiveFilters, setNumActiveFilters] = useState<number>(calculateActiveFilterCount(props.defaultFilters))

  const setFilters = (newFilters: LeadFilters) => {
    store.setItem(cacheKey, newFilters).finally(() => {
      setActiveFilters(newFilters)
      setNumActiveFilters(calculateActiveFilterCount(newFilters))
    })
  }

  useEffect(() => {
    store.getItem<LeadFilters>(cacheKey).then(storedFilters => {
      if (storedFilters && typeof storedFilters === 'object' && validateFilters(storedFilters)) {
        setActiveFilters(storedFilters)
        setNumActiveFilters(calculateActiveFilterCount(storedFilters))
      }
    })
  }, [cacheKey])

  const [{ data, loading }, { variables: dynamicQueryVariables, query: dynamicQueryDocument }] = useLeadsDataSource(
    offset,
    PAGE_SIZE,
    activeFilters
  )

  const {
    isOpen: assignModalIsOpen,
    open: openAssignModal,
    close: closeAssignModal,
    accountId: assignModalAccountId,
  } = useAssignModal()

  const {
    isOpen: profileAccountModalIsOpen,
    open: openProfileAccountModal,
    close: closeProfileAccountModal,
    accountId: profileAccountModalAccountId,
  } = useProfileModal()

  const { addLead, dismissLead, profileLead, assignLead, loading: actionLoading } = useLeadActions({
    refetchQueries: [{ query: dynamicQueryDocument, variables: dynamicQueryVariables }],
  })

  const leads = data?.dynamicQuery?.data || []

  const defaultFilters: LeadFilters = {
    targetCheckSize: targetCheckSize.map(x => x.value),
    netWorth: netWorth.map(x => x.value),
    leadStatus: props.defaultFilters.leadStatus,
    dateSignedUp: activeFilters.dateSignedUp,
  }

  return (
    <Fragment>
      <Box m={0} p={0}>
        <Box px={3} py={2} pb={30} className={classes.contentArea}>
          <CardTable
            filter={(hideModal: () => void) => (
              <LeadsDashboardFilter
                defaultFilters={defaultFilters}
                filters={activeFilters}
                onFilterChange={setFilters}
                hideModal={hideModal}
              />
            )}
            numActiveFilters={numActiveFilters}
            header={props.title}
            colDefs={columnDefs}
            count={data ? data.dynamicQuery.count : 0}
            offset={offset}
            pageSize={PAGE_SIZE}
            onPageChange={(newOffset: number) => setOffset(newOffset)}
            onSort={() => void 0}>
            {leads.map(lead => (
              <LeadsTableRow
                key={lead.id}
                lead={lead}
                openAssignModal={openAssignModal}
                openProfileAccountModal={openProfileAccountModal}
                addLead={addLead}
                dismissLead={dismissLead}
                currentAdmin={currentAdmin}
              />
            ))}
            {actionLoading && (
              <div className={classes.loading}>
                <CircularProgress color="secondary" />
              </div>
            )}
            {!data && loading ? (
              <Card>
                <CardHeader title="Loading..." />
              </Card>
            ) : (
              data &&
              data.dynamicQuery.count === 0 && (
                <Card>
                  <CardHeader title="No Matches" />
                </Card>
              )
            )}
          </CardTable>
        </Box>
      </Box>
      <AssignModal
        open={assignModalIsOpen}
        onClose={closeAssignModal}
        account={leads.find(({ id }) => id === assignModalAccountId)}
        assignLead={assignLead}
      />
      <ProfileModal
        open={profileAccountModalIsOpen}
        onClose={closeProfileAccountModal}
        profileLead={profileLead}
        account={leads.find(({ id }) => id === profileAccountModalAccountId)}
      />
    </Fragment>
  )
}
